import type { DossierStatus } from "@/lib/supabase";

export interface DossierStatusMeta {
  status: DossierStatus;
  label: string;
  description: string;
  tone: "neutral" | "muted" | "brand" | "warning" | "success" | "danger";
  order: number;
  group: "draft" | "review" | "production" | "delivered" | "closed";
}

export const DOSSIER_STATUS_META: Record<DossierStatus, DossierStatusMeta> = {
  non_demarre: {
    status: "non_demarre",
    label: "Non démarré",
    description: "Le salarié n'a pas encore activé son dossier.",
    tone: "muted",
    order: 1,
    group: "draft",
  },
  demarre_non_complete: {
    status: "demarre_non_complete",
    label: "Démarré",
    description: "Le salarié a commencé à compléter son dossier.",
    tone: "muted",
    order: 2,
    group: "draft",
  },
  complete_incomplet: {
    status: "complete_incomplet",
    label: "Complété — incomplet",
    description: "Soumis mais des pièces ou informations manquent.",
    tone: "warning",
    order: 3,
    group: "review",
  },
  complete_complet: {
    status: "complete_complet",
    label: "Complété — complet",
    description: "Le dossier est prêt à passer en production.",
    tone: "brand",
    order: 4,
    group: "review",
  },
  en_production: {
    status: "en_production",
    label: "En production",
    description: "Le consultant Aon prépare votre Bilan Retraite.",
    tone: "brand",
    order: 5,
    group: "production",
  },
  a_restituer: {
    status: "a_restituer",
    label: "À restituer",
    description: "Le BRI est prêt — la restitution sera planifiée.",
    tone: "brand",
    order: 6,
    group: "production",
  },
  restitue_avec_rdv: {
    status: "restitue_avec_rdv",
    label: "Restitué (RDV)",
    description: "Le BRI a été restitué en rendez-vous.",
    tone: "success",
    order: 7,
    group: "delivered",
  },
  restitue_sans_rdv: {
    status: "restitue_sans_rdv",
    label: "Restitué",
    description: "Le BRI a été remis sans rendez-vous.",
    tone: "success",
    order: 8,
    group: "delivered",
  },
  maj_sans_restitution: {
    status: "maj_sans_restitution",
    label: "MAJ — sans restitution",
    description: "Une mise à jour du BRI a été produite sans nouvel échange.",
    tone: "neutral",
    order: 9,
    group: "delivered",
  },
  maj_avec_restitution: {
    status: "maj_avec_restitution",
    label: "MAJ — avec restitution",
    description: "Une mise à jour a été restituée.",
    tone: "success",
    order: 10,
    group: "delivered",
  },
  sans_suite: {
    status: "sans_suite",
    label: "Sans suite",
    description: "Le dossier a été clos sans BRI.",
    tone: "danger",
    order: 11,
    group: "closed",
  },
};

/** Étapes du parcours salarié (5 jalons macro pour visualisation steppers). */
export const DOSSIER_JOURNEY_STEPS = [
  {
    key: "demarrage",
    label: "Démarrage",
    description: "Activation du compte et premiers échanges.",
    statuses: ["non_demarre", "demarre_non_complete"] as DossierStatus[],
  },
  {
    key: "completion",
    label: "Complétion",
    description: "Documents, situation, scénarios.",
    statuses: ["complete_incomplet", "complete_complet"] as DossierStatus[],
  },
  {
    key: "production",
    label: "Production",
    description: "Calcul de la pension et préparation du BRI.",
    statuses: ["en_production"] as DossierStatus[],
  },
  {
    key: "restitution",
    label: "Restitution",
    description: "Présentation du BRI par un consultant Aon.",
    statuses: ["a_restituer", "restitue_avec_rdv", "restitue_sans_rdv"] as DossierStatus[],
  },
  {
    key: "suivi",
    label: "Suivi",
    description: "Mises à jour ultérieures du BRI.",
    statuses: ["maj_sans_restitution", "maj_avec_restitution"] as DossierStatus[],
  },
] as const;

export type JourneyStepKey = (typeof DOSSIER_JOURNEY_STEPS)[number]["key"];

/**
 * Tous les statuts possibles, ordonnés par progression métier.
 *
 * Les équipes AON peuvent passer librement à n'importe quel statut (pas de
 * machine à états restrictive) : on expose donc la liste complète.
 */
export const ALL_DOSSIER_STATUSES: DossierStatus[] = (
  Object.values(DOSSIER_STATUS_META) as DossierStatusMeta[]
)
  .slice()
  .sort((a, b) => a.order - b.order)
  .map((m) => m.status);

/**
 * Transitions de statut "recommandées" depuis le statut courant.
 *
 * Conservé pour information / usages futurs, mais l'UI laisse désormais le
 * choix libre parmi tous les statuts (cf. ALL_DOSSIER_STATUSES).
 */
export const DOSSIER_STATUS_TRANSITIONS: Record<DossierStatus, DossierStatus[]> = {
  non_demarre: ["demarre_non_complete"],
  demarre_non_complete: ["complete_incomplet", "complete_complet"],
  complete_incomplet: ["complete_complet", "demarre_non_complete"],
  complete_complet: ["en_production", "complete_incomplet"],
  en_production: ["a_restituer", "complete_complet"],
  a_restituer: ["restitue_avec_rdv", "restitue_sans_rdv"],
  restitue_avec_rdv: ["maj_avec_restitution"],
  restitue_sans_rdv: ["maj_sans_restitution"],
  maj_avec_restitution: ["restitue_avec_rdv"],
  maj_sans_restitution: ["restitue_sans_rdv"],
  sans_suite: [],
};

/**
 * Statuts considérés comme « dossier complet » (le salarié a fourni le
 * nécessaire et le dossier a atteint au moins l'étape de complétion validée).
 */
const COMPLETE_STATUSES: ReadonlySet<DossierStatus> = new Set<DossierStatus>([
  "complete_complet",
  "en_production",
  "a_restituer",
  "restitue_avec_rdv",
  "restitue_sans_rdv",
  "maj_sans_restitution",
  "maj_avec_restitution",
]);

/** Le dossier est-il « complet » (prêt à passer en production / restitution) ? */
export function isDossierComplete(status: DossierStatus): boolean {
  return COMPLETE_STATUSES.has(status);
}

/** Donne le step macro courant pour un statut donné. */
export function getCurrentJourneyStep(status: DossierStatus): JourneyStepKey | null {
  const step = DOSSIER_JOURNEY_STEPS.find((s) =>
    (s.statuses as readonly DossierStatus[]).includes(status),
  );
  return step?.key ?? null;
}

/** Donne la progression en % (0–100) pour un statut donné. */
export function getJourneyProgress(status: DossierStatus): number {
  const meta = DOSSIER_STATUS_META[status];
  if (!meta) return 0;
  if (meta.group === "closed") return 100;
  return Math.round((meta.order / 10) * 100);
}

/**
 * Le dossier est-il encore éditable par le salarié via le wizard ?
 *
 * Le wizard reste accessible tant que le dossier n'est pas passé en production.
 * Cela permet au salarié de corriger ses informations et de re-soumettre,
 * même après une première soumission.
 */
export function isDossierEditable(status: DossierStatus): boolean {
  const meta = DOSSIER_STATUS_META[status];
  if (!meta) return false;
  return meta.group === "draft" || meta.group === "review";
}
